/**
 * Sending view_item event for marketing spots of type content in batch instead of one by one.
 */

import { DO_NOT_UPDATE_ESPOT } from '@/components/content/Header/constants';
import { usePreviewWidget } from '@/components/preview/PreviewWidgetFrame';
import { getEMarketingSpotName } from '@/core/data/Content/_ESpotDataFromName';
import { useBreadcrumbTrail } from '@/data/Content/BreadcrumbTrail';
import { fetcher, getESpotDataFromName } from '@/data/Content/_ESpotDataFromName-Server';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useMarketingCookieContentTarget } from '@/data/Content/_MarketingCookieContentTarget';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useSettings } from '@/data/Settings';
import { usePageDataFromId } from '@/data/_PageDataFromId';
import { COOKIES } from '@/data/constants/cookie';
import { DATA_KEY_E_SPOT_DATA_FROM_NAME } from '@/data/constants/dataKey';
import { CookiesSingletonContext } from '@/data/cookie/cookiesSingletonProvider';
import { ID } from '@/data/types/Basic';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getDMSubstitutions } from '@/data/utils/getDMSubstitutions';
import { getESpotParams } from '@/data/utils/getESpotQueryParams';
import { shrink } from '@/data/utils/keyUtil';
import { laggyMiddleWare } from '@/data/utils/laggyMiddleWare';
import { marketingSpotMiddleWare } from '@/data/utils/swr/marketingContentMiddleWare';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { DEFAULT_STORE_ID, WC_DEFAULT_STORE_COOKIE } from 'shared/custom/constants/commonConstants';
import useSWR from 'swr';

export type { ESpotContainerType } from '@/data/types/ESpot';
export { getESpotDataFromName };

export const useESpotDataFromName = (emsName: ID, _trackEvents = true) => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const { breadcrumb } = useBreadcrumbTrail();
	const { storeId, langId, defaultCatalogId: catalogId } = getClientSideCommon(settings, router);
	const { setESpotData } = usePreviewWidget();
	const { query } = router;
	const params = useExtraRequestParameters(false);
	const { data: pageData } = usePageDataFromId();
	const DM_Substitution = useMemo(
		() => getDMSubstitutions(emsName, { pageData, settings, langId }),
		[emsName, langId, pageData, settings]
	);
	const { getCookie } = useContext(CookiesSingletonContext);
	const referrer = useMemo(() => getCookie<number>(COOKIES.referrer), [getCookie]);
	const marketingCookieTargets: Record<string, string> = useMarketingCookieContentTarget();

	const cookieTargets = useMemo(
		() => (DO_NOT_UPDATE_ESPOT.includes(emsName) ? {} : marketingCookieTargets),
		[emsName, marketingCookieTargets]
	);
	// This key help us to refresh the content if DefaultStore cookie change
	const refreshIfChangeKey = useMemo(() => {
		const currentStore = cookieTargets[WC_DEFAULT_STORE_COOKIE] ?? DEFAULT_STORE_ID;
		return { [WC_DEFAULT_STORE_COOKIE]: currentStore };
	}, [cookieTargets]);

	const buildQueryBase = useCallback(
		(extra = {}) => ({
			...(!DO_NOT_UPDATE_ESPOT.includes(emsName) && extra),
			catalogId,
			DM_ReturnCatalogGroupId: true,
			DM_FilterResults: false,
			// DM_DisplayContent: 1,
			langId,
			DM_Substitution,
		}),
		[catalogId, langId, DM_Substitution, emsName]
	);

	const queryBase = useMemo(
		() => ({
			...buildQueryBase(cookieTargets),
			...(referrer && { DM_RefUrl: referrer }),
		}),
		[buildQueryBase, cookieTargets, referrer]
	);

	const queryBaseKey = useMemo(
		() => buildQueryBase(refreshIfChangeKey),
		[buildQueryBase, refreshIfChangeKey]
	);

	const isStaticESpot = useMemo(() => DO_NOT_UPDATE_ESPOT.includes(emsName), [emsName]);

	const eSpotKey = useMemo(() => {
		if (!storeId) return null;

		return [
			shrink({
				storeId,
				...getESpotParams({
					pageData: isStaticESpot ? undefined : pageData,
					// Include path/searchTerm (not the full query) so the key — and thus the eSpot —
					// re-renders when the search term changes, without busting on facet changes.
					query: isStaticESpot ? {} : { path: query.path, searchTerm: query.searchTerm },
					emsName,
					queryBase: queryBaseKey,
					breadcrumb: isStaticESpot ? [] : breadcrumb,
				}),
			}),
			DATA_KEY_E_SPOT_DATA_FROM_NAME,
		];
	}, [
		storeId,
		isStaticESpot,
		pageData,
		emsName,
		queryBaseKey,
		breadcrumb,
		query.path,
		query.searchTerm,
	]);

	const { data, error, isLoading } = useSWR(
		eSpotKey,
		async ([]) => {
			const queryParams = {
				storeId,
				...getESpotParams({ pageData, query, emsName, queryBase, breadcrumb }),
			};
			return fetcher(true)(
				storeId,
				queryParams.emsName,
				queryParams.query as Record<string, any>,
				params
			);
		},
		{ use: [laggyMiddleWare, marketingSpotMiddleWare] }
	);

	useEffect(() => {
		setESpotData && setESpotData(data?.MarketingSpotData);
	}, [data, setESpotData]);

	return {
		data,
		loading: isLoading,
		error,
	};
};

// export core items
export { getEMarketingSpotName };
